import * as React from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import TextField from "@mui/material/TextField";
import SendIcon from "@mui/icons-material/Send";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import RemoveIcon from "@mui/icons-material/Remove";
import { SelectChangeEvent } from "@mui/material/Select";
import InputAdornment from "@mui/material/InputAdornment";
import SquareIcon from "@mui/icons-material/SquareRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import Select from "src/components/Select";

import agent from "src/helpers/apiAgent";

import { Generic } from "src/@types/Category";

import { trackAction } from "src/helpers/tracking";
import { successHandler, errorHandler } from "src/helpers/responseHandler";

import useCourse from "src/hooks/useCourse";

interface AddCategorySheetProps {
  course: Generic;
  currentCategories: Generic;
  editingCategoryId: string;
  addNewlyCreatedCategory: Generic;
  updateCategoryLocally: Generic;
  selectedCategory: { categoryId: string; categoryDepth: number };
}

const CategoryLevelColors = {
  1: "#0A75F5",
  2: "#f77320",
  3: "#7146f7",
};

const AddCategoryBottomSheet: React.FC<AddCategorySheetProps> = ({
  course,
  selectedCategory,
  currentCategories,
  editingCategoryId,
  updateCategoryLocally,
  addNewlyCreatedCategory,
}) => {
  const { t } = useTranslation();
  const { activeGrade } = useCourse();

  const [_, setLoading] = React.useState(false);
  const [categoryName, setCategoryName] = React.useState("");
  const [selectedCategorise, setSelectedCategorise] = React.useState({
    one: "",
    two: "",
    three: "",
  });
  const [categoriesOptions, setCategoriesOptions] = React.useState({
    levelOne: [],
    levelTwo: [],
    levelThree: [],
  });

  const formOptionsStructure = (c) => ({ label: c?.text, value: c?.id });

  // Empty state after closing
  React.useEffect(() => {
    return () => setCategoryName("");
  }, []);

  React.useEffect(() => {
    if (editingCategoryId) {
      const cat = currentCategories?.find((c) => c?.id === editingCategoryId);
      if (Object.keys(cat).length) setCategoryName(cat?.text);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingCategoryId]);

  // Forming Select dropdown values
  React.useEffect(() => {
    let levelOne = [];
    let levelTwo = [];
    let levelThree = [];
    if (
      selectedCategorise?.one ||
      selectedCategorise?.two ||
      selectedCategorise?.three
    ) {
      // When levels are selected then re-forming according to selection
      const { one, two } = selectedCategorise;

      levelOne = currentCategories?.filter((c) => c.parent === 0);

      if (one) {
        levelTwo = currentCategories?.filter((c) => c.parent === one);
      }
      if (two) {
        levelThree = currentCategories?.filter((c) => c.parent === two);
      }
    } else {
      // When nothing is selected forming level wise dropdowns
      levelOne = currentCategories?.filter((c) => c.parent === 0);
      levelTwo = currentCategories?.filter((c) =>
        levelOne?.some((o) => o?.id === c?.parent)
      );
      levelThree = currentCategories?.filter((c) =>
        levelTwo?.some((t) => t?.id === c?.parent)
      );
    }

    levelOne = levelOne?.map(formOptionsStructure);
    levelTwo = levelTwo?.map(formOptionsStructure);
    levelThree = levelThree?.map(formOptionsStructure);

    setCategoriesOptions({ levelOne, levelTwo, levelThree });
  }, [currentCategories, selectedCategorise]);

  // Determing bottom sheet Select selected value from Tree selection
  React.useEffect(() => {
    const child = currentCategories?.find(
      (c) => c.id === selectedCategory?.categoryId
    );
    const parent = currentCategories?.find((c) => c.id === child?.parent);
    const grandParent = currentCategories?.find((c) => c.id === parent?.parent);

    if (grandParent?.id) {
      setSelectedCategorise({
        one: grandParent?.id ?? parent?.id ?? child?.id ?? "",
        two: parent?.id ?? child?.id ?? "",
        three: child?.id ?? "",
      });
    } else if (parent?.id) {
      setSelectedCategorise({
        one: parent?.id ?? child?.id ?? "",
        two: child?.id ?? "",
        three: "",
      });
    } else if (child?.id) {
      setSelectedCategorise({
        one: child?.id ?? "",
        two: "",
        three: "",
      });
    }
  }, [currentCategories, selectedCategory]);

  const handleClose = () => {
    document
      .getElementById("category-btm-sheet")
      ?.classList.remove("active-blk");

    setTimeout(() => {
      const bottomSheet = document.getElementById("category-btm-sheet");
      if (bottomSheet) {
        bottomSheet.style.display = "none";
        setCategoryName("");
      }
    }, 1000);

    setSelectedCategorise({
      one: "",
      two: "",
      three: "",
    });
  };

  const handleChange = (event: SelectChangeEvent) =>
    setSelectedCategorise((prev) => ({
      ...prev,
      [event.target.name]: event.target.value,
    }));

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const name = categoryName;
      const parentId =
        selectedCategorise?.three ||
        selectedCategorise?.two ||
        selectedCategorise?.one ||
        null;
      const courseId = course?.id;

      if (name && courseId) {
        let response;
        if (editingCategoryId) {
          response = await agent.Category.updateCategory(
            editingCategoryId,
            courseId,
            parentId,
            name,
            activeGrade?.id
          );
        } else {
          response = await agent.Category.createCategory(
            courseId,
            parentId,
            name,
            activeGrade?.id
          );
        }

        if ([200, 201].includes(response.code)) {
          successHandler(response);
          if (editingCategoryId) {
            updateCategoryLocally(response.data, course?.id, editingCategoryId);
            trackAction("Category_Update", {
              course: course?.id,
              courseCode: course?.courseCode,
              categoryId: editingCategoryId,
              category: response?.data?.name,
            });
          } else {
            addNewlyCreatedCategory(response.data, course?.id);
            trackAction("Category_Create", {
              course: course?.id,
              courseCode: course?.courseCode,
              categoryId: response.data?.id,
              category: response?.data?.name,
            });
          }
          handleClose();
          setCategoryName("");
        }
      } else {
        errorHandler(t("errors.addCategoryParentOrName"));
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const addCategoryDepth = () => {
    if (
      selectedCategorise?.one &&
      selectedCategorise?.two &&
      selectedCategorise?.three
    ) {
      return;
    } else if (selectedCategorise?.one && selectedCategorise?.two) {
      setSelectedCategorise((prev) => ({ ...prev, three: "1" }));
    } else if (selectedCategorise?.one) {
      setSelectedCategorise((prev) => ({ ...prev, two: "1" }));
    }
  };

  const decreaseCategoryDepth = () => {
    if (selectedCategorise?.three && selectedCategorise?.two) {
      setSelectedCategorise((prev) => ({ ...prev, three: "" }));
    } else if (selectedCategorise?.one && selectedCategorise?.two) {
      setSelectedCategorise((prev) => ({ ...prev, two: "" }));
    }
  };

  return (
    <div id="category-btm-sheet">
      <div className="category-btm-sheet-wrap">
        <Grid container className="top-blk">
          <Grid item xs={12} className="align-center-corner">
            <Typography variant="h2" className="caption">
              {t("category.addNewCategory")}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseRoundedIcon />
            </IconButton>
          </Grid>
        </Grid>

        <Grid container>
          <Grid item xs={6} className="align-center-corner">
            <Typography variant="h3" className="sub-heading m0">
              {t("category.level")}
            </Typography>

            <Box className="level-icons">
              <IconButton onClick={addCategoryDepth}>
                <AddIcon />
              </IconButton>
              <IconButton onClick={decreaseCategoryDepth}>
                <RemoveIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>

        <Grid container className="btm-blk">
          {/* Experimental */}
          {categoriesOptions?.levelOne?.length &&
          selectedCategorise?.one === "" ? (
            <Box sx={{ display: "inline-flex" }}>
              <SquareIcon
                className="icon-square mr-10"
                sx={{ color: CategoryLevelColors[1] }}
              />
              <Select
                allowNone
                name={"one"}
                onChange={handleChange}
                value={selectedCategorise?.one}
                options={categoriesOptions?.levelOne}
              />
            </Box>
          ) : null}
          {/* Experimental */}

          {categoriesOptions?.levelOne?.length && selectedCategorise?.one ? (
            <Box sx={{ display: "inline-flex" }}>
              <SquareIcon
                className="icon-square mr-10"
                sx={{ color: CategoryLevelColors[1] }}
              />
              <Select
                allowNone
                name={"one"}
                onChange={handleChange}
                value={selectedCategorise?.one}
                options={categoriesOptions?.levelOne}
              />
            </Box>
          ) : null}
          {categoriesOptions?.levelTwo?.length && selectedCategorise?.two ? (
            <Box sx={{ display: "inline-flex" }}>
              <SquareIcon
                className="icon-square mr-10"
                sx={{ color: CategoryLevelColors[2] }}
              />
              <Select
                allowNone
                name={"two"}
                onChange={handleChange}
                value={selectedCategorise?.two}
                options={categoriesOptions?.levelTwo}
              />
            </Box>
          ) : null}
          {categoriesOptions?.levelThree?.length &&
          selectedCategorise?.three ? (
            <Box sx={{ display: "inline-flex" }}>
              <SquareIcon
                className="icon-square mr-10"
                sx={{ color: CategoryLevelColors[3] }}
              />
              <Select
                allowNone
                name={"three"}
                onChange={handleChange}
                value={selectedCategorise?.three}
                options={categoriesOptions?.levelThree}
              />
            </Box>
          ) : null}

          <Box className="custom-input-wrap bg-light">
            <Typography variant="h2" color="text.primary">
              {t("category.categoryName")}
            </Typography>
            <TextField
              variant="outlined"
              value={categoryName}
              onChange={(e) => setCategoryName(e?.target?.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handleSubmit}>
                      <SendIcon className="icon-send" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Grid>
      </div>
    </div>
  );
};

export default AddCategoryBottomSheet;
